import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { canManageRoles } from '../../constants/adminRoles';
import {
  ADMIN_USERS_PAGE_SIZE,
  buildBrowseRequest,
  buildLookupRequest,
  listAdminUsers,
} from '../../services/admin/usersService';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminRoleLabel,
  classifyAdminCallableError,
  formatAdminDate,
} from './adminUserDisplay';
import AdminChangeRoleDialog from './AdminChangeRoleDialog';
import {
  buildImpersonationReturnPath,
  reloadToAccountHome,
  setImpersonationReturnPath,
} from './impersonationNav';

function personaLabel(t, persona) {
  if (!persona) return ADMIN_DATE_PLACEHOLDER;
  const key = `persona.${persona}`;
  const label = t(key);
  return label === key ? persona : label;
}

function approvalLabel(t, status) {
  if (!status) return ADMIN_DATE_PLACEHOLDER;
  const key = `admin.users.approval.${status}`;
  const label = t(key);
  return label === key ? status : label;
}

function impersonateErrorMessage(kind, t) {
  if (kind === 'not-found') return t('admin.users.impersonate.error.notFound');
  if (kind === 'permission-denied') return t('admin.users.impersonate.error.denied');
  return t('admin.users.impersonate.error');
}

function UserRowActions({
  user,
  t,
  showRoleActions,
  impersonatingUid,
  onImpersonate,
  onChangeRole,
}) {
  return (
    <div className="flex flex-col items-start gap-1">
      <Link
        to={`/admin/users/${encodeURIComponent(user.uid)}`}
        className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {t('admin.users.open360')}
      </Link>
      {user.canImpersonate === true ? (
        <button
          type="button"
          disabled={impersonatingUid === user.uid}
          onClick={() => onImpersonate(user)}
          className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        >
          {impersonatingUid === user.uid
            ? t('admin.users.impersonate.busy')
            : t('admin.users.impersonate.short')}
        </button>
      ) : null}
      {showRoleActions ? (
        <button
          type="button"
          onClick={() => onChangeRole(user)}
          className="font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('admin.users.changeRole')}
        </button>
      ) : null}
    </div>
  );
}

function CardField({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{children}</dd>
    </div>
  );
}

export default function AdminUsersPage() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const { startImpersonation, adminRole } = useAuth();
  const role = adminRole || 'super_admin';
  const showRoleActions = canManageRoles(role);
  const [searchInput, setSearchInput] = useState('');
  const [mode, setMode] = useState('browse');
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null);
  const [roleSavedMessage, setRoleSavedMessage] = useState('');
  const [impersonatingUid, setImpersonatingUid] = useState(null);
  const [impersonateError, setImpersonateError] = useState('');

  const busy = status === 'loading' || status === 'paging';

  const loadBrowse = async (pageToken, { paging = false, nextStack } = {}) => {
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    try {
      const data = await listAdminUsers(
        buildBrowseRequest({ pageSize: ADMIN_USERS_PAGE_SIZE, pageToken }),
      );
      const list = Array.isArray(data?.users) ? data.users : [];
      setUsers(list);
      setMode('browse');
      setNextPageToken(data?.nextPageToken || null);
      setCurrentToken(pageToken || null);
      if (Array.isArray(nextStack)) setTokenStack(nextStack);
      else if (!paging) setTokenStack([]);
      setStatus(list.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      setUsers([]);
      setNextPageToken(null);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
    }
  };

  const runLookup = async (query) => {
    setStatus('loading');
    setErrorKind(null);
    setNextPageToken(null);
    setTokenStack([]);
    setCurrentToken(null);
    setMode('lookup');
    try {
      const data = await listAdminUsers(buildLookupRequest(query));
      const list = Array.isArray(data?.users) ? data.users : [];
      setUsers(list);
      setMode('lookup');
      setStatus(list.length === 0 ? 'lookup-empty' : 'ready');
    } catch (err) {
      setUsers([]);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
    }
  };

  useEffect(() => {
    loadBrowse(undefined);
  }, []);

  const handleSearch = async (event) => {
    event.preventDefault();
    const query = searchInput.trim();
    if (!query) return;
    await runLookup(query);
  };

  const handleReset = () => {
    setSearchInput('');
    setTokenStack([]);
    setCurrentToken(null);
    loadBrowse(undefined);
  };

  const handleNext = () => {
    if (mode !== 'browse' || !nextPageToken || busy) return;
    loadBrowse(nextPageToken, {
      paging: true,
      nextStack: [...tokenStack, currentToken],
    });
  };

  const handlePrevious = () => {
    if (mode !== 'browse' || tokenStack.length === 0 || busy) return;
    const nextStack = tokenStack.slice(0, -1);
    const previousToken = tokenStack[tokenStack.length - 1];
    loadBrowse(previousToken || undefined, { paging: true, nextStack });
  };

  const reloadCurrentList = async () => {
    if (mode === 'lookup') {
      const query = searchInput.trim();
      if (query) {
        await runLookup(query);
        return;
      }
    }
    await loadBrowse(currentToken || undefined, { paging: true, nextStack: tokenStack });
  };

  const handleImpersonate = async (user) => {
    if (!user || user.canImpersonate !== true || impersonatingUid) return;
    setImpersonateError('');
    setImpersonatingUid(user.uid);
    try {
      await startImpersonation({ targetUid: user.uid });
      setImpersonationReturnPath(buildImpersonationReturnPath(location));
      reloadToAccountHome();
    } catch (err) {
      setImpersonateError(impersonateErrorMessage(classifyAdminCallableError(err), t));
      setImpersonatingUid(null);
    }
  };

  const errorMessage =
    errorKind === 'unavailable'
      ? t('admin.users.error.unavailable')
      : errorKind === 'internal'
        ? t('admin.users.error.internal')
        : t('admin.users.error');

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-start gap-2">
        <Users className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('admin.users.title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('admin.users.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="min-w-0 flex-1 text-sm">
          <span className="mb-1 block text-slate-600">{t('admin.users.search.label')}</span>
          <input
            id="admin-users-search"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t('admin.users.search.placeholder')}
            autoComplete="off"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={busy || !searchInput.trim()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.users.search.submit')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={busy && status === 'loading'}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.users.reset')}
          </button>
        </div>
      </form>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.users.loading')}
        </p>
      ) : null}
      {status === 'paging' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.users.paging')}
        </p>
      ) : null}
      {status === 'empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.users.empty')}
        </p>
      ) : null}
      {status === 'lookup-empty' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.users.lookupEmpty')}
        </p>
      ) : null}
      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={() => (mode === 'lookup' ? handleSearch({ preventDefault() {} }) : loadBrowse(currentToken || undefined))}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.users.retry')}
          </button>
        </div>
      ) : null}

      {roleSavedMessage ? (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" role="status">
          {roleSavedMessage}
        </p>
      ) : null}
      {impersonateError ? (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
          {impersonateError}
        </p>
      ) : null}

      {users.length > 0 && (status === 'ready' || status === 'paging') ? (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">{t('admin.users.title')}</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.name')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.email')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.persona')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.approval')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.role')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.status')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.created')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.lastSignIn')}</th>
                  <th scope="col" className="px-3 py-2 font-semibold">{t('admin.users.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.uid} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2">{user.displayName || ADMIN_DATE_PLACEHOLDER}</td>
                    <td className="px-3 py-2 break-all">{user.email || ADMIN_DATE_PLACEHOLDER}</td>
                    <td className="px-3 py-2">{personaLabel(t, user.persona)}</td>
                    <td className="px-3 py-2">{approvalLabel(t, user.personaApprovalStatus)}</td>
                    <td className="px-3 py-2">{adminRoleLabel(user.rentalyzerRole, t)}</td>
                    <td className="px-3 py-2">
                      {user.disabled ? t('admin.users.status.disabled') : t('admin.users.status.enabled')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(user.creationTime, language)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAdminDate(user.lastSignInTime, language)}</td>
                    <td className="px-3 py-2">
                      <UserRowActions
                        user={user}
                        t={t}
                        showRoleActions={showRoleActions}
                        impersonatingUid={impersonatingUid}
                        onImpersonate={handleImpersonate}
                        onChangeRole={setRoleTarget}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {users.map((user) => (
              <article key={user.uid} className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <p className="font-semibold text-slate-900">{user.displayName || ADMIN_DATE_PLACEHOLDER}</p>
                <p className="mt-0.5 break-all text-slate-600">{user.email || ADMIN_DATE_PLACEHOLDER}</p>
                <dl className="mt-3 grid grid-cols-2 gap-3">
                  <CardField label={t('admin.users.col.persona')}>{personaLabel(t, user.persona)}</CardField>
                  <CardField label={t('admin.users.col.approval')}>
                    {approvalLabel(t, user.personaApprovalStatus)}
                  </CardField>
                  <CardField label={t('admin.users.col.role')}>{adminRoleLabel(user.rentalyzerRole, t)}</CardField>
                  <CardField label={t('admin.users.col.status')}>
                    {user.disabled ? t('admin.users.status.disabled') : t('admin.users.status.enabled')}
                  </CardField>
                  <CardField label={t('admin.users.col.created')}>
                    {formatAdminDate(user.creationTime, language)}
                  </CardField>
                  <CardField label={t('admin.users.col.lastSignIn')}>
                    {formatAdminDate(user.lastSignInTime, language)}
                  </CardField>
                </dl>
                <div className="mt-3">
                  <UserRowActions
                    user={user}
                    t={t}
                    showRoleActions={showRoleActions}
                    impersonatingUid={impersonatingUid}
                    onImpersonate={handleImpersonate}
                    onChangeRole={setRoleTarget}
                  />
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      {mode === 'browse' && (status === 'ready' || status === 'paging' || status === 'empty') ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={busy || tokenStack.length === 0}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.users.previous')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={busy || !nextPageToken}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {t('admin.users.next')}
          </button>
        </div>
      ) : null}

      <AdminChangeRoleDialog
        open={Boolean(roleTarget)}
        user={roleTarget}
        t={t}
        onClose={() => setRoleTarget(null)}
        onSaved={() => {
          setRoleTarget(null);
          setRoleSavedMessage(t('admin.users.changeRole.success'));
          reloadCurrentList();
        }}
      />
    </div>
  );
}
